/**
 * EKSPERTİZ RAPORU OKUYUCU
 * ========================
 * Sigorta eksperinin PDF raporundan araç bilgisi, değişen parçalar,
 * onarılan / boyanan parçalar, işçilik ve toplamları çıkarır; hasar hesabı
 * formuna işlenecek biçimde döndürür. Hiçbir şey kaydetmez.
 *
 * Desteklenen biçimler (gerçek raporlarla denendi):
 *   - "Kasko/Trafik Ekspertiz Raporu" tablolu biçim: PARÇA BİLGİLERİ
 *     (kısım no, parça adı, parça kodu, adet, KDV'siz fiyat, iskonto,
 *     toplam) ve İŞÇİLİK BİLGİLERİ (kaporta / boya / elektrik / diğer).
 *   - SBM "Kesin Ekspertiz Raporu": Onarım Montaj İşçilikleri tablosu
 *     (parça, işlem, kaporta, mekanik, elektrik, döşeme, cam, kalibrasyon,
 *     onarım, boya) ve Hesap Özeti.
 *   - OtoHasar çıktısı (Aksigorta vb.): "Değişmesi Gereken Hasarlı Parça ve
 *     Malzemeler" ve "İşçilik Hesap Özeti" tabloları; İngilizce rakam yazımı.
 *     Tablolar PDF'teki konumlarla okunur (bkz. bicimOtohasar).
 *   Bilinmeyen biçimde en azından "Hesap Özeti" toplamları alınır.
 *
 * PDF metni pdfjs ile okunur (taranmış görüntü PDF'lerde metin yoktur;
 * o durumda kullanıcıya söylenir). Türkçe harfler bazı raporlarda bozuk
 * çıkar ("oN TAMPON", "cAMURLUK"); karşılaştırma bu yüzden sadeleştirilmiş
 * büyük harfle yapılır.
 */

/* ---------------- Metin yardımcıları ---------------- */

const HARF = { ı: 'I', i: 'I', İ: 'I', ş: 'S', Ş: 'S', ğ: 'G', Ğ: 'G', ü: 'U', Ü: 'U', ö: 'O', Ö: 'O', ç: 'C', Ç: 'C', â: 'A', î: 'I', û: 'U' };
/** "oN TAMPON BRAKETi" → "ON TAMPON BRAKETI" */
export const sade = (s) => String(s ?? '')
  .normalize('NFC') // Mac / iPhone'dan gelen adlarda "ü" iki parça (u + ¨) olabiliyor
  .replace(/[ıiİşŞğĞüÜöÖçÇâîû]/g, (h) => HARF[h] || h)
  .toUpperCase()
  .replace(/\s+/g, ' ')
  .trim();

/*
 * Ekranda gösterilecek parça adı. Bazı raporların PDF'inde Türkçe büyük
 * harfler küçük ASCII harf olarak çıkıyor: "oN" = ÖN, "cAMURLUK" = ÇAMURLUK,
 * "BRAKETi" = BRAKETİ, "KAPAgI" = KAPAĞI, "SENSoRu" = SENSÖRÜ. Büyük harfli
 * bir kelimenin içindeki bu küçük harfler karşılığına çevrilir; tamamen ASCII
 * yazılmış sık kelimeler de sözlükten düzeltilir.
 */
const KUCUK_TR = { o: 'Ö', c: 'Ç', i: 'İ', s: 'Ş', g: 'Ğ', u: 'Ü' };
const SOZLUK = {
  ON: 'ÖN', CAMURLUK: 'ÇAMURLUK', SAG: 'SAĞ', GOGUS: 'GÖĞÜS', SIS: 'SİS', CERCEVE: 'ÇERÇEVE', CERCEVESI: 'ÇERÇEVESİ',
  DEMIRI: 'DEMİRİ', BRAKETI: 'BRAKETİ', UST: 'ÜST', USTU: 'ÜSTÜ', KAPAGI: 'KAPAĞI', SENSORU: 'SENSÖRÜ', RADYATOR: 'RADYATÖR',
  DIREGI: 'DİREĞİ', ICI: 'İÇİ', IC: 'İÇ', DIS: 'DIŞ', KOSE: 'KÖŞE', GOVDE: 'GÖVDE', ONARIM: 'ONARIM', DOSEME: 'DÖŞEME',
  DAVLUMBAZI: 'DAVLUMBAZI', KAPUTU: 'KAPUTU', SACI: 'SACI', MENTESE: 'MENTEŞE', MENTESESI: 'MENTEŞESİ', TAKOZU: 'TAKOZU',
};
export const adDuzelt = (s) => String(s ?? '')
  .split(/\s+/)
  .filter(Boolean)
  .map((k) => {
    const buyuk = (k.match(/[A-ZÇĞİÖŞÜ]/g) || []).length;
    const kucuk = (k.match(/[a-zçğıöşü]/g) || []).length;
    let w = k;
    if (buyuk > 0 && kucuk > 0 && buyuk >= kucuk) w = k.replace(/[ocisgu]/g, (h) => KUCUK_TR[h]);
    w = w.toLocaleUpperCase('tr');
    return SOZLUK[w] || w;
  })
  .join(' ');

/**
 * "9.870,11" / "68752.26" / "1.600.000" / "28.250,00" → sayı.
 * İngilizce yazım da okunur (OtoHasar raporları): "129,465.95", "1,350,000.00",
 * km "39,353". Hem nokta hem virgül varsa sondaki ondalık ayracıdır.
 */
export const tutarOku = (s) => {
  let t = String(s ?? '').replace(/TL/gi, '').replace(/\s/g, '').trim();
  if (!t) return 0;
  const virgul = t.lastIndexOf(',');
  const nokta = t.lastIndexOf('.');
  if (virgul >= 0 && nokta >= 0) {
    t = nokta > virgul ? t.replace(/,/g, '') : t.replace(/\./g, '').replace(',', '.');
  } else if (virgul >= 0) {
    // Yalnız virgül: "39,353" / "1,350,000" binlik; "12,50" / "0,125" ondalık
    t = /^[1-9]\d{0,2}(,\d{3})+$/.test(t) ? t.replace(/,/g, '') : t.replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 0;
};

// İlk seçenek İngilizce yazım (129,465.95): yoksa "465.95" diye yarım okunuyordu.
const TUTAR = '(\\d{1,3}(?:,\\d{3})+\\.\\d{2}|\\d{1,3}(?:\\.\\d{3})*,\\d{2}|\\d+\\.\\d{2}|\\d+,\\d{2})';

/* ---------------- PDF → satırlar (pdfjs) ---------------- */

let pdfjsSozu = null;
const pdfjsYukle = () => {
  if (!pdfjsSozu) {
    pdfjsSozu = import('pdfjs-dist/build/pdf.min.mjs').then((mod) => {
      const lib = mod?.default ?? mod;
      lib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return lib;
    });
  }
  return pdfjsSozu;
};

/**
 * PDF baytlarından, sayfa sayfa yukarıdan aşağı satırlar.
 * Dizinin "konum" alanında her satırın sayfası, yüksekliği ve parçaları
 * (x, genişlik, metin) da durur: bazı rapor tablolarında bir rakamın hangi
 * sütuna ait olduğu yalnızca yatay konumdan anlaşılıyor (bkz. bicimOtohasar).
 */
export async function pdfSatirlari(veri, pdfjs = null) {
  const lib = pdfjs || await pdfjsYukle();
  const belge = await lib.getDocument({ data: veri instanceof Uint8Array ? veri : new Uint8Array(veri) }).promise;
  const satirlar = [];
  const konum = [];
  for (let s = 1; s <= belge.numPages; s += 1) {
    const sayfa = await belge.getPage(s);
    const icerik = await sayfa.getTextContent();
    const gruplar = [];
    for (const it of icerik.items) {
      if (!it.str || !it.str.trim()) continue;
      const y = it.transform[5];
      const x = it.transform[4];
      let g = gruplar.find((k) => Math.abs(k.y - y) < 2.5);
      if (!g) { g = { y, parcalar: [] }; gruplar.push(g); }
      g.parcalar.push({ x, w: it.width || 0, str: it.str });
    }
    gruplar.sort((a, b) => b.y - a.y);
    for (const g of gruplar) {
      g.parcalar.sort((a, b) => a.x - b.x);
      satirlar.push(g.parcalar.map((p) => p.str).join(' ').replace(/\s+/g, ' ').trim());
      konum.push({ sayfa: s, y: g.y, parcalar: g.parcalar });
    }
  }
  Object.defineProperty(satirlar, 'konum', { value: konum, enumerable: false });
  return satirlar;
}

/* ---------------- Parça adı → hesap kataloğu ---------------- */

const taraf = (a) => (/\bSAG\b/.test(a) ? 'sag' : /\bSOL\b/.test(a) ? 'sol' : '');

/**
 * Eksperin parça adını hesaptaki parça koduna eşler. Emin olunamayan
 * parçalar "Diğer parça" (X.25) olur; adı satırda korunur.
 */
export function parcaKodu(adHam) {
  const a = sade(adHam);
  const t = taraf(a);
  const has = (...k) => k.every((x) => a.includes(x));
  const yan = (sag, sol, yok = 'X.25') => (t === 'sag' ? sag : t === 'sol' ? sol : yok);

  if (has('BAKALIT') || has('IZOLASYON') || has('FITIL') || has('KILIT') || has('KILID') || has('MENTESE') || has('KOLU') || has('DOSEME')) {
    return has('DAVLUMBAZ') ? 'X.24' : 'X.25';
  }
  if (has('DAVLUMBAZ') || has('IC KAPLAMA')) return 'X.24';
  if (has('HAVA YASTI') || has('AIRBAG')) {
    if (has('SURUCU') || has('DIREKSIYON')) return 'A.30';
    if (has('YOLCU')) return 'A.29';
    return yan('A.31', 'A.32', 'A.29');
  }
  if (has('SUBAP') || has('SUBAB')) return 'X.25'; // "LASTİK SUBABI" lastik değil
  if (has('TAMPON')) {
    if (has('BRAKET') || has('DEMIR') || has('BAGLANTI') || has('TAKOZ') || has('TUTUCU') || has('KUSAK')
      || has('KOPUK') || has('KOPUG') || has('SPOYLER')) return 'X.3';
    return has('ARKA') ? 'X.2' : 'X.1';
  }
  if (has('PANJUR') || has('IZGARA')) return 'X.4';
  if (has('FAR') && !has('FARK')) {
    if (has('SIS')) return 'X.7';
    if (has('YIKAMA') || has('FISKIYE') || has('JET') || has('YUVA') || has('AYAR')) return 'X.25';
    return yan('X.5', 'X.6');
  }
  if (has('STOP') || has('ARKA LAMBA')) return yan('X.8', 'X.9');
  if (has('AYNA')) return yan('X.10', 'X.11');
  if (has('CAM') && !has('CAMURLUK')) {
    if (has('ON CAM') || has('ON TAMPON CAM')) return 'X.12';
    if (has('ARKA CAM') || has('BAGAJ CAM')) return 'X.13';
    return 'X.14';
  }
  if (has('JANT')) return 'X.15';
  if (has('LASTIK')) return 'X.16';
  if (has('RADYATOR') || has('KONDENSER') || has('INTERCOOLER')) return 'X.17';
  if (has('AMORTISOR') || has('SALINCAK') || has('SUSPANSIYON') || has('ROTIL') || has('Z ROT')) return 'X.18';
  if (has('ROT') || has('AKS') || has('PORYA') || has('DIREKSIYON KUTU')) return 'X.19';
  if (has('FREN') || has('BALATA') || has('KALIPER') || has('DISK')) return 'X.20';
  if (has('MOTOR KULAK') || has('SANZIMAN') || has('KARTER') || has('ALTERNATOR') || has('KOMPRESOR')) return 'X.21';
  if (has('SENSOR') || has('KAMERA') || has('RADAR') || has('BEYIN') || has('MODUL')) return 'X.23';
  if (has('KAPUT')) return 'A.10';
  if (has('CAMURLUK')) return has('ARKA') ? yan('A.23', 'A.24') : yan('A.3', 'A.4');
  if (has('PODYE')) return yan('A.5', 'A.6');
  if (has('GOGUS')) return 'A.9';
  // "YAN PANEL KOMPLE SOL": aracın yan dış sacı; arka çamurluğu da içerir.
  if (has('YAN PANEL')) return yan('A.23', 'A.24');
  if (has('PANEL')) return has('ARKA') ? 'A.22' : 'A.2';
  if (has('MARSPIYEL')) return yan('A.15', 'A.16');
  if (has('DIREK')) return has('B DIREK') ? yan('A.18', 'A.20') : yan('A.17', 'A.19');
  if (has('KAPI') && !has('BAGAJ')) return has('ARKA') ? yan('A.13', 'A.14') : yan('A.11', 'A.12');
  if (has('BAGAJ') && (has('KAPAK') || has('KAPAG') || has('KAPI'))) return 'A.21';
  if (has('HAVUZ') || has('BAGAJ TABAN')) return 'A.25';
  if (has('TAVAN')) return 'A.1';
  if (has('SASE')) return has('ARKA') ? yan('A.26', 'A.27') : yan('A.7', 'A.8');
  if (has('TRAVERS') || has('DINGIL')) return 'A.28';
  return 'X.25';
}

/* ---------------- Alan okuma ---------------- */

/** "Etiket : değer" satırları; aynı etiket iki kez varsa ilki (talep eden araç önce gelir). */
function alanlar(satirlar) {
  const out = [];
  for (const s of satirlar) {
    // Bir satırda iki "etiket : değer" olabilir: "Rapor No : TR15/074 Rapor/Kayıt Tarihi : 11.03.2025 ..."
    const re = /([A-ZÇĞİÖŞÜa-zçğıöşü'’()\-/. ]{2,60}?)\s*:\s*(.*?)(?=\s+[A-ZÇĞİÖŞÜ][A-ZÇĞİÖŞÜa-zçğıöşü'’()\-/. ]{1,40}\s:\s|$)/g;
    let m;
    while ((m = re.exec(s)) !== null) {
      const etiket = sade(m[1]);
      const deger = (m[2] || '').trim();
      if (etiket) out.push({ etiket, deger });
      if (m.index === re.lastIndex) re.lastIndex += 1;
    }
  }
  return out;
}
/** Etiketi TAM eşleşen ilk değer (raporlarda talep eden araç, sigortalı araçtan önce gelir). */
const bul = (liste, ...adlar) => {
  for (const ad of adlar) {
    const k = liste.find((x) => x.etiket === sade(ad));
    if (k && k.deger && !/^[-–—]+$/.test(k.deger)) return k.deger;
  }
  return '';
};

const tarihIso = (s) => {
  const m = String(s || '').match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
};
const plakaDuzelt = (s) => {
  const t = sade(s).replace(/[\s-]/g, '').replace(/^0(\d{2})/, '$1'); // "06-DMH-466" de olur
  const m = t.match(/^(\d{2})([A-Z]{1,3})(\d{2,5})$/);
  return m ? `${m[1]} ${m[2]} ${m[3]}` : sade(s);
};
const GRUP = [['KAMYONET', 'kamyonet'], ['PANELVAN', 'kamyonet'], ['KAMYON', 'kamyon'], ['MINIBUS', 'minibus'], ['OTOBUS', 'otobus'],
  ['MOTOSIKLET', 'motosiklet'], ['CEKICI', 'cekici'], ['TRAKTOR', 'traktor'], ['ROMORK', 'romork'], ['TAKSI', 'taksi'], ['OTOMOBIL', 'otomobil'], ['ARAZI', 'otomobil'],
  ['OZEL OTO', 'otomobil']];
const grupBul = (...metinler) => {
  for (const m of metinler) {
    const s = sade(m);
    const g = GRUP.find(([k]) => s.includes(k));
    if (g) return g[1];
  }
  return '';
};

/* ---------------- Biçim 1: PARÇA BİLGİLERİ / İŞÇİLİK BİLGİLERİ ---------------- */

const PARCA_SATIRI = new RegExp(`^(\\d{1,2})\\s+(.+?)\\s+([A-Z0-9][A-Z0-9\\-]{4,})\\s+(\\d{1,3})\\s+${TUTAR}\\s*(?:TL\\s*)?${TUTAR}\\s*(?:TL\\s*)?${TUTAR}`);
const ISCILIK_SATIRI = new RegExp(`^(\\d{1,2})\\s+(.+?)\\s+${TUTAR}\\s*(?:TL\\s*)?${TUTAR}\\s*(?:TL\\s*)?${TUTAR}`);

function bolum(satirlar, baslangic, bitisler) {
  const i = satirlar.findIndex((s) => sade(s).includes(baslangic));
  if (i < 0) return [];
  const out = [];
  for (let j = i + 1; j < satirlar.length; j += 1) {
    const s = sade(satirlar[j]);
    if (bitisler.some((b) => s.includes(b))) break;
    out.push(satirlar[j]);
  }
  return out;
}

/** Tablo satırları ve altlarına taşan ad parçaları ("BRAKETi SOL TL TL") birleştirilir. */
function tabloSatirlari(satirlar, desen) {
  const kayitlar = [];
  for (const ham of satirlar) {
    const s = ham.trim();
    const m = s.match(desen);
    if (m) { kayitlar.push({ m, ek: [] }); continue; }
    if (!kayitlar.length) continue;
    const temiz = s.replace(/\bTL\b/g, '').trim();
    if (!temiz || /^(KISIM|NO|NUMARASI|FIYAT|FİYAT|TUTARI|TAKIP|TAKİP)\b/i.test(temiz)) continue;
    if (/\d+,\d{2}/.test(temiz)) continue;
    kayitlar[kayitlar.length - 1].ek.push(temiz);
  }
  return kayitlar;
}

function bicimTablolu(satirlar, sonuc) {
  const parcaBolum = bolum(satirlar, 'PARCA BILGILERI', ['YEDEK PARCA TOPLAMI', 'ISCILIK BILGILERI']);
  for (const { m, ek } of tabloSatirlari(parcaBolum, PARCA_SATIRI)) {
    const adHam = [m[2], ...ek].join(' ');
    const ad = adDuzelt(adHam);
    sonuc.parcalar.push({
      ad, kod: parcaKodu(adHam), islem: 'degisim', boya: 'yok',
      adet: Number(m[4]) || 1,
      bedel: tutarOku(m[5]) / (Number(m[4]) || 1),
      iskonto: tutarOku(m[6]),
      tutar: tutarOku(m[7]),
    });
  }

  const iscBolum = bolum(satirlar, 'ISCILIK BILGILERI', ['ISCILIK BILGILERI TOPLAMI', 'EKSPERTIZ MASRAF', 'HESAP OZETI']);
  for (const { m, ek } of tabloSatirlari(iscBolum, ISCILIK_SATIRI)) {
    const metin = sade([m[2], ...ek].join(' ').replace(/\bISCILI(GI|K)\b/g, ''));
    const net = tutarOku(m[5]);
    let tur = 'diger';
    if (/^KAPORTA/.test(metin)) tur = 'kaporta';
    else if (/^BOYA/.test(metin)) tur = 'boya';
    else if (/^ELEKTRIK/.test(metin)) tur = 'elektrik';
    else if (/^MEKANIK/.test(metin)) tur = 'mekanik';
    sonuc.iscilik[tur] += net;
    sonuc.iscilikKalemleri.push({ tur, metin, brut: tutarOku(m[3]), iskonto: tutarOku(m[4]), net });

    // Hangi parçaya yapıldığı: "BOYA ON TAMPON", "KAPORTA SOKTAK MOTOR KAPUTU", "KAPORTA ONARIM ..."
    const parcaAdi = metin
      .replace(/^(KAPORTA|BOYA|ELEKTRIK|MEKANIK|DIGER)\s*/, '')
      .replace(/\b(SOKTAK|SOK TAK|BOKTAK|ISCILIGI|ISCILIK|ELEKTRIK|BOYA|ONARIM|DIGER)\b/g, ' ')
      .replace(/\s+/g, ' ').trim();
    if (parcaAdi) {
      const islem = tur === 'boya' ? 'boya' : (tur === 'kaporta' && /ONARIM/.test(metin) && !/SOK ?TAK|BOKTAK/.test(metin)) ? 'onarim' : null;
      if (islem) parcaIsle(sonuc, parcaAdi, islem);
    }
  }
}

/** Boyanan / onarılan parçayı listeye ekler ya da değişen parçaya boyayı işler. */
function parcaIsle(sonuc, ad, islem) {
  const kod = parcaKodu(ad);
  const mevcut = sonuc.parcalar.find((p) => (kod !== 'X.25' && p.kod === kod) || sade(p.ad) === sade(ad));
  if (mevcut) {
    if (islem === 'boya') mevcut.boya = 'tam';
    if (islem === 'onarim' && mevcut.islem !== 'degisim') mevcut.islem = 'onarim';
    return;
  }
  sonuc.parcalar.push({ ad: adDuzelt(ad), kod, islem, boya: islem === 'boya' ? 'tam' : 'yok', adet: 1, bedel: 0, iskonto: 0, tutar: 0 });
}

/* ---------------- Biçim 2: SBM Onarım Montaj İşçilikleri ---------------- */

// "X21 SOL ÖN ÇAMURLUK 0,00 Onarım 3.500,00 0,00 0,00 0,00 0,00 0,00 1.500,00 5.500,00 10.500,00"
const SBM_ISCILIK = new RegExp(`^(\\S+)\\s+(.+?)\\s+${TUTAR}\\s+(Onar[ıi]m|De[ğg]i[şs]im|S[öo]k\\s*Tak|Boya|Ayar)\\s+((?:${TUTAR}\\s*){9})`, 'i');

function bicimSbm(satirlar, sonuc) {
  const iscBolum = bolum(satirlar, 'ONARIM MONTAJ ISCILIKLERI', ['TOPLAM ISCILIK TUTARI', 'HESAP OZETI']);
  const kayitlar = [];
  for (const ham of iscBolum) {
    const m = ham.trim().match(SBM_ISCILIK);
    if (m) { kayitlar.push({ m, ek: [] }); continue; }
    if (kayitlar.length && !/\d+,\d{2}/.test(ham) && sade(ham) && !/^(ORIJINAL|BEDELI|KILIT|RASYON)/.test(sade(ham))) {
      kayitlar[kayitlar.length - 1].ek.push(ham.trim());
    }
  }
  for (const { m, ek } of kayitlar) {
    const ad = sade([m[2], ...ek].join(' '));
    const islemHam = sade(m[4]);
    const kolonlar = (m[5].match(new RegExp(TUTAR, 'g')) || []).map(tutarOku);
    // Kaporta, Mekanik, Elektrik, Döş/Kilit, Cam, Kalibrasyon, Onarım, Boya, Toplam
    const [kap, mek, elk, dos, cam, kal, ona, boy] = kolonlar;
    sonuc.iscilik.kaporta += (kap || 0) + (ona || 0);
    sonuc.iscilik.mekanik += mek || 0;
    sonuc.iscilik.elektrik += elk || 0;
    sonuc.iscilik.diger += (dos || 0) + (cam || 0) + (kal || 0);
    sonuc.iscilik.boya += boy || 0;
    sonuc.iscilikKalemleri.push({ tur: 'satir', metin: ad, net: kolonlar[8] || 0 });

    const islem = /DEGISIM/.test(islemHam) ? 'degisim' : /ONARIM/.test(islemHam) ? 'onarim' : /BOYA/.test(islemHam) ? 'boya' : 'sokTak';
    const parcaBedeli = tutarOku(m[3]);
    const mevcut = sonuc.parcalar.find((p) => sade(p.ad) === ad);
    if (mevcut) {
      if (boy > 0) mevcut.boya = 'tam';
      continue;
    }
    sonuc.parcalar.push({
      ad: adDuzelt([m[2], ...ek].join(' ')), kod: parcaKodu(ad), islem, boya: boy > 0 ? 'tam' : 'yok', adet: 1,
      bedel: islem === 'degisim' ? parcaBedeli : 0, iskonto: 0, tutar: islem === 'degisim' ? parcaBedeli : 0,
    });
  }
}

/* ---------------- Biçim 3: OtoHasar çıktısı (Aksigorta vb.) ---------------- */

/*
 * "III-) HASAR TESPİTİ — A) DEĞİŞMESİ GEREKEN HASARLI PARÇA VE MALZEMELER" ve
 * "İŞÇİLİK HESAP ÖZETİ" tablolu rapor (otohasar.aksigorta.com.tr çıktısı; OtoHasar
 * sistemini kullanan şirketlerde aynı biçim). Tutarlar İngilizce yazımla gelir
 * (129,465.95). Gerçek raporla denendi (2026-10-06).
 *
 * Düz metin yetmiyor: parça adı rakamların yazdığı satırın üstüne ve altına
 * bölünüyor, işçilik rakamlarının hangi sütuna (onarım, boya, kaporta, mekanik…)
 * ait olduğu da yalnızca yatay konumdan anlaşılıyor. Bu yüzden pdfSatirlari'nin
 * verdiği konumlarla okunur: rakam sütunu, başlıktaki sütun adının sol kenarına
 * göre bulunur (rakamlar sütunun sağına yaslı).
 */
const ING_TUTAR = /^(?:\d{1,3}(?:,\d{3})+|\d+)\.\d{2}$/;
const yuvarla = (n) => Math.round(n * 100) / 100;

/** Başlık satırındaki sütun adlarının sol kenarları: [[anahtar, x], …] (x'e göre sıralı). */
function sutunlar(satir, adlar) {
  const out = [];
  for (const p of satir.parcalar) {
    const t = sade(p.str);
    for (const [anahtar, ad] of adlar) if (t === ad || t.startsWith(`${ad} `)) out.push([anahtar, p.x]);
  }
  return out.sort((a, b) => a[1] - b[1]);
}

/** Satırdaki tutarlar, sağ kenarına göre ait olduğu sütunla: { anahtar: tutar } */
function hucreler(satir, sutun) {
  const out = {};
  for (const p of satir.parcalar) {
    const son = p.str.trim().split(/\s+/).pop();
    if (!ING_TUTAR.test(son)) continue;
    const sagKenar = p.x + (p.w || 0);
    let anahtar = null;
    for (const [k, x] of sutun) if (x < sagKenar) anahtar = k;
    if (anahtar) out[anahtar] = (out[anahtar] || 0) + tutarOku(son);
  }
  return out;
}

/** Verilen x aralığındaki metin parçaları (yukarıdan aşağı, soldan sağa). */
const araliktakiMetin = (satirlar, x1, x2) => satirlar
  .slice().sort((a, b) => b.y - a.y)
  .flatMap((s) => s.parcalar.filter((p) => p.x >= x1 - 1 && p.x < x2).map((p) => p.str))
  .join(' ').replace(/\s+/g, ' ').trim();

/**
 * Tablo bölgesini kayıtlara ayırır: rakam satırı kaydın kendisidir; rakamsız
 * satırlar (adın taşan parçaları) aynı sayfada dikeyde en yakın rakam satırına eklenir.
 */
function konumluKayitlar(konum, bas, son, rakamSatiriMi, atla) {
  const veri = [];
  const parca = [];
  for (let i = bas; i < son; i += 1) {
    if (atla(i)) continue;
    (rakamSatiriMi(konum[i]) ? veri : parca).push(konum[i]);
  }
  const kayitlar = veri.map((s) => ({ satir: s, hepsi: [s] }));
  for (const p of parca) {
    let en = null;
    let fark = Infinity;
    for (const k of kayitlar) {
      if (k.satir.sayfa !== p.sayfa) continue;
      const d = Math.abs(k.satir.y - p.y);
      if (d < fark) { fark = d; en = k; }
    }
    if (en && fark < 14) en.hepsi.push(p);
  }
  return kayitlar;
}

// Parça adının içine düşen notlar: "(X)", "SİSTEMDEN KÜÇÜK", "Fatura Fiyati Dusuk", "(Harici Malzeme)"
const parcaNotlariniAt = (s) => String(s)
  .replace(/\(\s*X\s*\)/gi, ' ')
  .replace(/S[İI]STEMDEN\s+(K[ÜU][ÇC][ÜU]K|B[ÜU]Y[ÜU]K)/gi, ' ')
  .replace(/\bS[İI]STEMDEN\b/gi, ' ')
  .replace(/Fatura\s+Fiyat[ıi]\s+D[üu][şs][üu]k/gi, ' ')
  .replace(/\(\s*Harici\s+Malzeme\s*\)/gi, ' ')
  .replace(/-\s*\(\s*\)/g, ' ')
  .replace(/\(\s+/g, '(').replace(/\s+\)/g, ')')
  .replace(/\s+/g, ' ')
  .trim();

function bicimOtohasar(satirlar, sonuc) {
  const konum = satirlar.konum;
  if (!konum || konum.length !== satirlar.length) {
    sonuc.uyarilar.push('Rapor tabloları okunamadı; yalnız toplamlar alınabildi.');
    return;
  }
  const sadeler = satirlar.map(sade);
  const bul1 = (bas, kosul) => sadeler.findIndex((s, i) => i > bas && kosul(s));

  /* ---- A) Değişen parçalar ---- */
  const pBas = sadeler.findIndex((s) => s.includes('DEGISMESI GEREKEN HASARLI PARCA'));
  const pSon = pBas < 0 ? -1 : bul1(pBas, (s) => /PARCA ?MALZEME TUTARI|ONARIM VE MONTAJ ISCILIKLERI/.test(s));
  const pBaslik = pBas < 0 ? -1 : bul1(pBas, (s) => s.includes('KULLANILAN') && s.includes('SISTEM'));
  if (pBas >= 0 && pSon > pBas && pBaslik > pBas && pBaslik < pSon) {
    const sutun = sutunlar(konum[pBaslik], [['ref', 'REF.'], ['isk', 'ISK.%'], ['fiyat', 'PARCA FIYATI'], ['sistem', 'SISTEM'], ['kullanilan', 'KULLANILAN']]);
    const x = Object.fromEntries(sutun);
    // Parça adı sütunu: üstteki başlık satırının ikinci "PARÇA"sı ("PARÇA KODU" / "PARÇA ADI")
    const ustBaslik = konum[pBaslik - 1]?.parcalar.filter((p) => sade(p.str) === 'PARCA') || [];
    const adX = ustBaslik[1]?.x ?? 80;
    if (x.kullanilan && x.ref) {
      const kayitlar = konumluKayitlar(konum, pBaslik + 1, pSon,
        (s) => hucreler(s, sutun).kullanilan != null,
        (i) => /^(KODU|ADI|NO\.)\b/.test(sadeler[i]) || /^KODU ADI/.test(sadeler[i]));
      for (const k of kayitlar) {
        const h = hucreler(k.satir, sutun);
        const kod = k.satir.parcalar.filter((p) => p.x < adX - 1).map((p) => p.str).join(' ').trim();
        const adHam = parcaNotlariniAt(araliktakiMetin(k.hepsi, adX, x.ref));
        const tutar = yuvarla(h.kullanilan);
        // İskontosuz bedel: "(*) SİSTEMDEN KÜÇÜK" satırlarında iskonto sistem fiyatına değil
        // daha düşük fatura fiyatına uygulanmış; bedel kullanılan fiyattan geri bulunur.
        // Böylece toplam, rapordaki "Hasar Gören Parçalar Karşılığı" ile tutar.
        const yildizli = k.satir.parcalar.some((p) => p.str.includes('(*)'));
        const isk = h.isk || 0;
        const bedel = yuvarla(h.sistem && !yildizli ? h.sistem : (isk > 0 && isk < 100 ? tutar / (1 - isk / 100) : (h.sistem || tutar)));
        sonuc.parcalar.push({
          ad: adDuzelt(adHam) || kod, kod: parcaKodu(adHam), islem: 'degisim', boya: 'yok', adet: 1,
          bedel, iskonto: yuvarla(Math.max(0, bedel - tutar)), tutar,
        });
      }
    }
  }

  /* ---- B) İşçilik hesap özeti ---- */
  const iBaslik = sadeler.findIndex((s) => s.includes('ISCILIK ISLEM') && s.includes('TOPLAM'));
  const iToplam = iBaslik < 0 ? -1 : bul1(iBaslik, (s) => /^TOPLAM\s+[\d,]+\.\d{2}/.test(s));
  if (iBaslik >= 0 && iToplam > iBaslik) {
    const sutun = sutunlar(konum[iBaslik], [['islem', 'ISLEM'], ['hdur', 'H.DUR'], ['ref', 'REF.'], ['ona', 'ONA.'], ['boya', 'BOYA'],
      ['kap', 'KAP.'], ['mek', 'MEK.'], ['elek', 'ELEK.'], ['dos', 'DOS/KIL.'], ['cam', 'CAM'], ['dig', 'DIG.'], ['toplam', 'TOPLAM']]);
    const x = Object.fromEntries(sutun);
    const rakamSutunlari = sutun.filter(([k]) => !['islem', 'hdur', 'ref'].includes(k));
    if (x.ona && x.toplam && x.islem) {
      const kayitlar = konumluKayitlar(konum, iBaslik + 1, iToplam,
        (s) => Object.keys(hucreler(s, rakamSutunlari)).length > 0,
        (i) => sadeler[i].startsWith('PARCA ORJINAL FIYATI'));

      const kolon = (h, ...k) => k.reduce((t, a) => t + (h[a] || 0), 0);
      const toplamSatiri = hucreler(konum[iToplam], rakamSutunlari);
      const satirToplami = {};
      for (const k of kayitlar) {
        const h = hucreler(k.satir, rakamSutunlari);
        for (const [a, v] of Object.entries(h)) satirToplami[a] = (satirToplami[a] || 0) + v;
        const ad = araliktakiMetin(k.hepsi, 0, x.islem);
        const islemMetni = sade(araliktakiMetin(k.hepsi, x.islem, x.hdur ?? x.ref));
        const hdur = sade(araliktakiMetin(k.hepsi, x.hdur ?? x.ref, x.ref ?? x.ona));
        sonuc.iscilikKalemleri.push({ tur: 'satir', metin: sade(`${ad} ${islemMetni}`), net: h.toplam || 0 });
        otohasarParcaIsle(sonuc, ad, islemMetni, hdur, (h.boya || 0) > 0);
      }

      // Sütun toplamları: rapordaki TOPLAM satırı (yoksa satırların toplamı)
      const t = Object.keys(toplamSatiri).length ? toplamSatiri : satirToplami;
      const brut = {
        kaporta: kolon(t, 'kap', 'ona'), boya: kolon(t, 'boya'), mekanik: kolon(t, 'mek'),
        elektrik: kolon(t, 'elek'), diger: kolon(t, 'dos', 'cam', 'dig'),
      };
      const brutToplam = Object.values(brut).reduce((a, b) => a + b, 0);
      if (t.toplam && Math.abs(t.toplam - brutToplam) >= 1) {
        sonuc.uyarilar.push('İşçilik sütunları rapordaki toplamla tutmuyor; işçiliği kontrol edin.');
      }

      // İşçilik iskontosu: "İSKONTO 18,674.99" / "İŞÇ. TOPLAM 105,824.97"
      const sonra = (desen) => {
        const j = bul1(iToplam - 1, (s) => desen.test(s));
        return j > 0 && j < iToplam + 6 ? tutarOku((satirlar[j].match(/([\d,]+\.\d{2})\s*$/) || [])[1]) : 0;
      };
      const iskonto = sonra(/^ISKONTO\b/);
      const net = sonra(/^ISC\.? ?TOPLAM\b/) || (brutToplam - iskonto);
      const oran = brutToplam > 0 ? net / brutToplam : 1;
      // Kategorilere oranla dağıtılır; kuruş farkı en büyük kategoriye yazılır.
      const sirali = Object.keys(brut).filter((k) => brut[k] > 0).sort((a, b) => brut[b] - brut[a]);
      let kalan = yuvarla(net);
      for (const k of sirali.slice(1)) {
        const v = yuvarla(brut[k] * oran);
        sonuc.iscilik[k] += v;
        kalan = yuvarla(kalan - v);
      }
      if (sirali.length) sonuc.iscilik[sirali[0]] += kalan;
      if (iskonto > 0) {
        sonuc.iscilikKalemleri.push({ tur: 'iskonto', metin: 'İŞÇİLİK İSKONTOSU', brut: yuvarla(brutToplam), iskonto, net: yuvarla(net) });
      }
    }
  }

  // OHOM (dışarıda yaptırılan onarım, ör. jant): işçiliğe eklenir.
  const ohom = sadeler.findIndex((s) => /^OHOM TUTARI\s*:/.test(s));
  if (ohom >= 0) {
    const v = tutarOku((satirlar[ohom].match(/([\d,]+\.\d{2})\s*$/) || [])[1]);
    if (v > 0) {
      sonuc.iscilik.diger += v;
      sonuc.iscilikKalemleri.push({ tur: 'diger', metin: 'OHOM (DIŞARIDA ONARIM)', net: v });
    }
  }
  for (const k of Object.keys(sonuc.iscilik)) sonuc.iscilik[k] = yuvarla(sonuc.iscilik[k]);
}

/**
 * İşçilik satırı → parça listesi: değişen parçaya boya işlenir; onarılan parça
 * (derecesiyle: HAFİF / ORTA / AĞIR) eklenir. Parçaya bağlı olmayan işçilik
 * ("ROT BALANS", "… MEKANİK İŞÇİLİK") atlanır.
 */
function otohasarParcaIsle(sonuc, adHam, islemMetni, hdur, boyali) {
  let ad = sade(adHam);
  const sokTak = /SOK ?TAK/.test(ad) || /SOK ?TAK/.test(islemMetni);
  ad = ad.replace(/\bSOK ?TAK\b/g, ' ').replace(/\s+/g, ' ').trim();
  if (!ad || /ISCILIK/.test(ad)) return;
  const islem = /DEGISIM/.test(islemMetni) ? 'degisim' : sokTak ? 'sokTak' : /ONARIM/.test(islemMetni) ? 'onarim' : null;
  if (!islem) return;
  const seviye = /HAFIF/.test(hdur) ? 'hafif' : /ORTA/.test(hdur) ? 'orta' : /AGIR|YUKSEK/.test(hdur) ? 'yuksek' : null;

  const kod = parcaKodu(ad);
  const mevcut = sonuc.parcalar.find((p) => sade(p.ad) === ad)
    || (kod !== 'X.25' ? sonuc.parcalar.find((p) => p.kod === kod) : null);
  if (mevcut) {
    if (boyali) mevcut.boya = 'tam';
    if (islem === 'onarim' && mevcut.islem !== 'degisim') {
      mevcut.islem = 'onarim';
      if (seviye) mevcut.onarimSeviyesi = seviye;
    }
    return;
  }
  sonuc.parcalar.push({
    ad: adDuzelt(ad), kod, islem, boya: boyali ? 'tam' : 'yok', adet: 1, bedel: 0, iskonto: 0, tutar: 0,
    ...(seviye ? { onarimSeviyesi: seviye } : {}),
  });
}

/* ---------------- Toplamlar ---------------- */

function toplamlar(satirlar, sonuc) {
  const al = (...etiketler) => {
    for (const e of etiketler) {
      // "KDV" etiketi "KDV'SİZ FİYAT TOPLAM" satırını yakalamasın: etiketten sonra harf gelmemeli.
      const s = satirlar.find((x) => {
        const t = sade(x);
        const k = sade(e);
        return t.startsWith(k) && !/^[A-Z’']/.test(t.slice(k.length));
      });
      if (s) {
        const m = s.match(new RegExp(`${TUTAR}\\s*(?:TL)?\\s*$`)) || s.match(/:\s*([\d.,]+)\s*$/);
        if (m) return tutarOku(m[1]);
      }
    }
    return null;
  };
  sonuc.toplamlar = {
    parca: al('YEDEK PARCA TOPLAM :', 'Yedek Parça Tutar', 'KDV’SİZ FİYAT TOPLAM', "KDV'SİZ FİYAT TOPLAM",
      'PARÇA-MALZEME TUTARI (A)', 'A) YEDEK PARÇA TUTARI'),
    iscilik: al('İŞÇİLİK TOPLAMI', 'İskontolu İşçilik Tutarı', 'İSKONTOLU İŞÇİLİK TUTARI',
      'İŞÇİLİKLER TUTARI (B)', 'B) İŞÇİLİK TUTARI'),
    kdv: al('KDV Tutarı', 'KDV'),
    genel: al('GENEL TOPLAM'),
    masraf: al('EKSPERTİZ MASRAF TOPLAMI', 'MASRAF TOPLAM'),
  };
}

/* ---------------- Ana fonksiyon ---------------- */

export function ekspertizAyristir(satirlar) {
  const metin = sade(satirlar.join(' '));
  const sonuc = {
    bicim: 'bilinmiyor',
    arac: {},
    parcalar: [],
    iscilik: { kaporta: 0, boya: 0, mekanik: 0, elektrik: 0, diger: 0 },
    iscilikKalemleri: [],
    iskontoOrani: null,
    toplamlar: {},
    uyarilar: [],
  };
  if (!metin) {
    sonuc.uyarilar.push('PDF\'te yazı yok (taranmış görüntü olabilir).');
    return sonuc;
  }
  if (!/EKSPER/.test(metin)) sonuc.uyarilar.push('Bu belge ekspertiz raporuna benzemiyor.');

  const a = alanlar(satirlar);
  const markaTip = bul(a, 'Markası ve Tipi');
  const [marka2, tip2] = markaTip ? markaTip.split('/').map((x) => x.trim()) : [];
  // OtoHasar: "MODELİ VE CİNSİ : 2023-Hususi"
  const modelCinsi = bul(a, 'Model Yılı - Cinsi', 'Modeli ve Cinsi');
  const kullanimMetni = sade(`${bul(a, 'Kullanım Şekli')} ${modelCinsi}`);
  // SBM raporunda önce "Tazminat Talep Eden Araç" (müvekkilin aracı), sonra "Sigortalı Araç"
  // gelir; "Markası ve Tipi" ve "Model Yılı - Cinsi" talep eden araca aittir.
  sonuc.arac = {
    marka: sade(marka2 || bul(a, 'Marka', 'Markası') || ''),
    // "HYUNDAI i20 1.4 MPI 100 STYLE 6AT" gibi ayraçsız yazımda tip, markayla birlikte
    // bütün metindir; marka adı ruhsattan doldururken baştan atılır (useTsb).
    tip: sade(tip2 || bul(a, 'Araç Tip', 'Araç Tipi', 'Tipi') || (markaTip && !markaTip.includes('/') ? markaTip : '')),
    modelYili: (modelCinsi || bul(a, 'Model', 'Model Yılı') || '').match(/\b(19|20)\d{2}\b/)?.[0] || '',
    plaka: plakaDuzelt(bul(a, 'Plaka', 'Plaka Numarası')),
    km: String(tutarOku(bul(a, 'Kilometre', "KM'si", 'KM’si', 'Aracın Kilometresi')) || ''),
    kullanim: /TICARI/.test(kullanimMetni) ? 'ticari' : /HUSUSI|OZEL OTO/.test(kullanimMetni) ? 'hususi' : '',
    grup: grupBul(bul(a, 'Araç Tarife Grubu'), modelCinsi.split('/')[1] || '', bul(a, 'Kullanım Şekli')),
    piyasaBedeli: tutarOku(bul(a, 'Araç Piyasa Bedeli', 'Araç Rayiç Bedeli', 'Piyasa Değeri')) || null,
    hasarTarihi: tarihIso(bul(a, 'Hasar Tarihi', 'Hasar Tarihi - Saati')),
    onarimGun: (bul(a, 'Hasar Onarım Süresi (Gün)', 'Hasar Onarım Süresi').match(/\d+/) || [''])[0],
  };
  // Yakıt raporda yazmaz; tip adındaki motor ibaresinden çıkarılır (D-4D, TDI, CRDI …).
  const tipSade = `${sonuc.arac.tip} ${sonuc.arac.marka}`;
  if (/\b(D-?4D|TDI|CRDI|DCI|HDI|BLUEHDI|MULTIJET|JTD|JTDM|CDI|TDCI|DIZEL|DIESEL|D-?CAT|CRD|SDI|ECOBLUE)\b/.test(tipSade)) sonuc.arac.yakit = 'dizel';
  else if (/\b(HYBRID|HIBRIT|HEV|PHEV)\b/.test(tipSade)) sonuc.arac.yakit = 'hibrit';
  else if (/\bLPG\b/.test(tipSade)) sonuc.arac.yakit = 'lpg';
  // Elektrikli: motor hacmi (1.6 gibi) olmadan kW yazan adlar (TOGG "160KW") ya da açık ibare.
  else if (/\b(ELEKTRIK|ELECTRIC|KWH|E-TRON)\b/.test(tipSade) || (/\d{2,3}\s?KW\b/.test(tipSade) && !/\b\d\.\d\b/.test(tipSade))) sonuc.arac.yakit = 'elektrik';
  // Raporda açıkça yazıyorsa o esas alınır (OtoHasar: "YAKIT TİPİ : Benzin", "LPG SISTEMI : Var").
  const yakitTipi = sade(bul(a, 'Yakıt Tipi', 'Yakıt Türü'));
  if (/DIZEL|DIESEL|MOTORIN/.test(yakitTipi)) sonuc.arac.yakit = 'dizel';
  else if (/HIBRIT|HYBRID/.test(yakitTipi)) sonuc.arac.yakit = 'hibrit';
  else if (/ELEKTRIK/.test(yakitTipi)) sonuc.arac.yakit = 'elektrik';
  else if (/LPG/.test(yakitTipi) || /^VAR\b/.test(sade(bul(a, 'LPG Sistemi')))) sonuc.arac.yakit = 'lpg';
  else if (/BENZIN/.test(yakitTipi)) sonuc.arac.yakit = 'benzin';

  // Bazı SBM raporlarında hasar tarihi ay/gün yer değiştirmiş basılıyor
  // ("03.11.2025" ama metinde "11/03/2025 tarihinde"). Hasar tarihi rapor ya da
  // ekspertiz tarihinden SONRA olamaz; öyleyse gün ile ay yer değiştirilir.
  const referans = ['Oluşturulma Tarihi', 'Rapor/Kayıt Tarihi', '1. Ekspertiz Tarihi', 'Ekspertiz Tarihi']
    .map((e) => tarihIso(bul(a, e))).filter(Boolean).sort()[0];
  if (sonuc.arac.hasarTarihi && referans && sonuc.arac.hasarTarihi > referans) {
    const [y, m, g] = sonuc.arac.hasarTarihi.split('-');
    const ters = `${y}-${g}-${m}`;
    if (Number(g) <= 12 && ters <= referans) {
      sonuc.uyarilar.push(`Rapordaki hasar tarihi (${g}.${m}.${y}) rapor tarihinden sonra görünüyor; gün ile ay yer değiştirmiş sayıldı: ${m}.${g}.${y}. Kontrol edin.`);
      sonuc.arac.hasarTarihi = ters;
    }
  }

  // Karşı tarafın (sigortalının) kusuru: "Sigortalı Kusur Oranı : %100"
  const kusur = bul(a, 'Sigortalı Kusur Oranı').match(/\d+/);
  if (kusur) sonuc.arac.karsiKusur = kusur[0];

  const iskonto = bul(a, 'Parça İskonto').match(/[\d.,]+/);
  if (iskonto) sonuc.iskontoOrani = tutarOku(iskonto[0]);

  if (metin.includes('PARCA BILGILERI') || metin.includes('ISCILIK BILGILERI')) {
    sonuc.bicim = 'tablolu';
    bicimTablolu(satirlar, sonuc);
  }
  if (metin.includes('ONARIM MONTAJ ISCILIKLERI')) {
    sonuc.bicim = sonuc.bicim === 'tablolu' ? 'karma' : 'sbm';
    bicimSbm(satirlar, sonuc);
  }
  if (sonuc.bicim === 'bilinmiyor' && metin.includes('DEGISMESI GEREKEN HASARLI PARCA') && metin.includes('ISCILIK HESAP OZETI')) {
    sonuc.bicim = 'otohasar';
    bicimOtohasar(satirlar, sonuc);
  }
  toplamlar(satirlar, sonuc);

  // Okunan kalemler rapordaki toplamlarla tutuyor mu?
  const parcaTop = sonuc.parcalar.reduce((t, p) => t + (p.tutar || 0), 0);
  const iscTop = Object.values(sonuc.iscilik).reduce((t, v) => t + v, 0);
  const yakin = (x, y) => y == null || Math.abs(x - y) < 1;
  sonuc.dogrulama = {
    parca: { okunan: Math.round(parcaTop * 100) / 100, rapor: sonuc.toplamlar.parca, tutuyor: yakin(parcaTop, sonuc.toplamlar.parca) },
    iscilik: { okunan: Math.round(iscTop * 100) / 100, rapor: sonuc.toplamlar.iscilik, tutuyor: yakin(iscTop, sonuc.toplamlar.iscilik) },
  };
  if (sonuc.bicim === 'bilinmiyor') sonuc.uyarilar.push('Rapor biçimi tanınmadı; yalnız toplamlar alınabildi.');
  if (!sonuc.dogrulama.parca.tutuyor) sonuc.uyarilar.push('Okunan parça tutarları rapordaki toplamla tutmuyor; parçaları kontrol edin.');
  if (!sonuc.dogrulama.iscilik.tutuyor) sonuc.uyarilar.push('Okunan işçilik rapordaki toplamla tutmuyor; işçiliği kontrol edin.');
  return sonuc;
}

/** PDF dosyası (File / Blob / ArrayBuffer) → ayrıştırılmış rapor. */
export async function ekspertizOku(dosya) {
  const veri = dosya instanceof ArrayBuffer ? dosya : await dosya.arrayBuffer();
  const satirlar = await pdfSatirlari(veri);
  return ekspertizAyristir(satirlar);
}
